import useI18n from '@/shared/hooks/usei18n'
import { Button, CheckboxGroup, InputGroup, SelectGroup, EU_COUNTRIES } from '@/common'
import { orderBy } from 'es-toolkit'
import { useCallback, useMemo, useState } from 'react'
import DeclarantDatePickerSingle from '@/shared/components/DeclarantDatePickerSingle'
import z from 'zod'

const inwardProcessingInfoSchema = z
  .object({
    memberState: z.string().refine(value => EU_COUNTRIES.includes(value.toLowerCase()), {
      message: 'Must be a valid EU member state code'
    }),
    inwardProcessingWaiver: z.boolean(),
    authorisation: z.string().max(512),
    globalisationTimeStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
    globalisationTimeEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
    billOfDischargeDeadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
  })
  .refine(data => new Date(data.globalisationTimeStart) <= new Date(data.globalisationTimeEnd), {
    message: 'Globalisation time start must be less or equal to the Globalisation time end',
    path: ['globalisationTimeStart']
  })
  .refine(
    data => {
      const globalisationEnd = new Date(data.globalisationTimeEnd)
      const billDeadline = new Date(data.billOfDischargeDeadline)
      const maxDeadline = new Date(globalisationEnd)
      maxDeadline.setDate(globalisationEnd.getDate() + 30)
      return billDeadline >= globalisationEnd && billDeadline <= maxDeadline
    },
    {
      message:
        'The deadline for the bill of discharge must be equal or greater (max. 30 days) than the globalisation time end',
      path: ['billOfDischargeDeadline']
    }
  )

export default function AddInwardProcessingInformationV2({
  back,
  add
}: {
  back: () => void
  add: (payload: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }) => void
}) {
  const initialState = {
    memberState: null,
    inwardProcessingWaiver: false,
    authorisation: null,
    globalisationTimeStart: null,
    globalisationTimeEnd: null,
    billOfDischargeDeadline: null
  }

  const { t } = useI18n()
  const [state, setState] = useState<{
    memberState: string | null
    inwardProcessingWaiver: boolean
    authorisation: string | null
    globalisationTimeStart: string | null
    globalisationTimeEnd: string | null
    billOfDischargeDeadline: string | null
  }>(initialState)

  const memberStatesOpotions = orderBy(
    EU_COUNTRIES.map(value => ({
      label: t(`countriesByIsoAlpha2.${value}`),
      value: value.toUpperCase()
    })),
    [option => option.label.toLowerCase().normalize('NFKD')],
    ['asc']
  )

  const onMemberStateChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setState({ ...state, memberState: evt.target.value })
  }
  const onAuthorisationChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, authorisation: evt.target.value })
  }
  const onInwardProcessingWaiverChange = (value: boolean) => {
    setState({ ...state, inwardProcessingWaiver: value })
  }
  const updateDate = (date: string, field: string) => {
    setState({
      ...state,
      [field]: date
    })
  }

  const validateState = useMemo(() => {
    return inwardProcessingInfoSchema.safeParse(state)
  }, [state])

  const isAddEnabled = validateState.success

  const onAdd = useCallback(() => {
    if (!validateState.success) {
      return
    }

    add(validateState.data)
    back()
  }, [validateState, add, back])

  return (
    <>
      <div className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="w-3/5">
            <SelectGroup
              value={state.memberState || ''}
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.memberState'
              )}
              options={memberStatesOpotions}
              onChange={onMemberStateChange}
            />
          </div>
          <div className="my-2">
            <CheckboxGroup
              id="inwardProcessingWaiver"
              checked={state.inwardProcessingWaiver || false}
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.inwardProcessingWaiver'
              )}
              onChange={onInwardProcessingWaiverChange}
            />
          </div>
        </div>
        <InputGroup
          value={state.authorisation || ''}
          label={t(
            'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.authorisation'
          )}
          onChange={onAuthorisationChange}
        />
        <div className="flex gap-4">
          <div className="grow">
            <DeclarantDatePickerSingle
              id="globalisationTimeStart"
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.globalisationTimeStart'
              )}
              onChange={(date: string) => updateDate(date, 'globalisationTimeStart')}
              date={state.globalisationTimeStart || undefined}
            />
          </div>
          <div className="grow">
            <DeclarantDatePickerSingle
              id="globalisationTimeEnd"
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.globalisationTimeEnd'
              )}
              onChange={(date: string) => updateDate(date, 'globalisationTimeEnd')}
              date={state.globalisationTimeEnd || undefined}
            />
          </div>
        </div>
        <div className="w-1/2 pr-2">
          <DeclarantDatePickerSingle
            id="billOfDischargeDeadline"
            label={t(
              'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.inputs.billOfDischargeDeadline'
            )}
            onChange={(date: string) => updateDate(date, 'billOfDischargeDeadline')}
            date={state.billOfDischargeDeadline || undefined}
          />
        </div>
        <div className="flex justify-end gap-4">
          <Button onClick={back} variant="subtle">
            {t('common:actions.back')}
          </Button>
          <Button onClick={onAdd} disabled={!isAddEnabled}>
            {t(
              'cbamReporting:cbamReportDetails.importedGoodCreateDialog.actions.addInwardProcessingInformation'
            )}
          </Button>
        </div>
      </div>
    </>
  )
}
